import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, message,
  Card, Tabs, Tag, Space, Popconfirm, Descriptions, Row, Col
} from 'antd';
import {
  PlusOutlined, SyncOutlined, DeleteOutlined, EditOutlined,
  EyeOutlined, KeyOutlined, UserOutlined
} from '@ant-design/icons';
import api from '../utils/api';

const { TabPane } = Tabs;
const { Option } = Select;

const BusinessManagers = () => {
  const [bms, setBms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBm, setEditingBm] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedBm, setSelectedBm] = useState(null);
  const [assets, setAssets] = useState({ pixels: [], pages: [], ad_accounts: [], users: [] });
  const [syncUsersLoading, setSyncUsersLoading] = useState(null);
  const [devAccountModalVisible, setDevAccountModalVisible] = useState(false);
  const [devAccountList, setDevAccountList] = useState([]);
  const [devAccountLoading, setDevAccountLoading] = useState(false);
  const [form] = Form.useForm();

  // 获取 BM 列表
  const fetchBMs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/business-managers/');
      setBms(response.data);
    } catch (error) {
      message.error('获取 BM 列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBMs();
  }, []);

  // 创建/更新 BM
  const handleSubmit = async (values) => {
    try {
      if (editingBm) {
        await api.put(`/business-managers/${editingBm.id}`, values);
        message.success('更新成功');
      } else {
        await api.post('/business-managers/', values);
        message.success('创建成功');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingBm(null);
      fetchBMs();
    } catch (error) {
      message.error(error.response?.data?.detail || '操作失败');
    }
  };

  // 删除 BM
  const handleDelete = async (id) => {
    try {
      await api.delete(`/business-managers/${id}`);
      message.success('删除成功');
      fetchBMs();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 同步 BM 资产
  const handleSync = async (id) => {
    try {
      const response = await api.post(`/business-managers/${id}/sync`);
      message.success(`同步成功：像素 ${response.data.pixels_synced} 个，主页 ${response.data.pages_synced} 个，账户 ${response.data.accounts_synced} 个`);
      fetchBMs();
    } catch (error) {
      message.error('同步失败：' + (error.response?.data?.detail || '未知错误'));
    }
  };

  // 同步 BM 用户
  const handleSyncUsers = async (id) => {
    setSyncUsersLoading(id);
    try {
      const response = await api.post(`/business-managers/${id}/sync-users`);
      const r = response.data.results || {};
      message.success(
        `用户同步成功：共 ${r.total || 0} 个（系统用户 ${r.system_user_count || 0}，普通用户 ${r.business_user_count || 0}）` +
          (r.probe_ad_account_id ? `，探测账户 act_${r.probe_ad_account_id}` : '')
      );
      fetchBMs();
    } catch (error) {
      message.error('同步用户失败：' + (error.response?.data?.detail || '未知错误'));
    } finally {
      setSyncUsersLoading(null);
    }
  };

  // 查看资产详情
  const handleViewAssets = async (bm) => {
    setSelectedBm(bm);
    try {
      const response = await api.get(`/business-managers/${bm.id}/assets`);
      console.log('Assets response:', response.data);
      setAssets({
        pixels: response.data.pixels || [],
        pages: response.data.pages || [],
        ad_accounts: response.data.ad_accounts || [],
        users: response.data.users || [],
      });
      setDetailVisible(true);
    } catch (error) {
      message.error('获取资产详情失败');
    }
  };

  // 查看BM详情（包含开发者账号）
  const handleViewDetail = async (bm) => {
    setSelectedBm(bm);
    try {
      const [detailRes, assetsRes] = await Promise.all([
        api.get(`/business-managers/${bm.id}/detail`),
        api.get(`/business-managers/${bm.id}/assets`),
      ]);
      setAssets({
        ...detailRes.data,
        developer_accounts: detailRes.data.developer_accounts || [],
        pixels: assetsRes.data.pixels || [],
        pages: assetsRes.data.pages || [],
        ad_accounts: assetsRes.data.ad_accounts || [],
        users: assetsRes.data.users || [],
      });
      setDetailVisible(true);
    } catch (error) {
      message.error('获取BM详情失败');
    }
  };

  // 查看BM关联的开发者账号列表
  const handleViewDevAccounts = async (bm) => {
    setSelectedBm(bm);
    setDevAccountLoading(true);
    try {
      const response = await api.get(`/business-managers/${bm.id}/developer-accounts`);
      console.log('Developer accounts response:', response.data);
      setDevAccountList(response.data || []);
      setDevAccountModalVisible(true);
    } catch (error) {
      message.error('获取开发者账号列表失败');
    } finally {
      setDevAccountLoading(false);
    }
  };

  // 打开编辑弹窗并加载完整参数
  const handleEdit = async (record) => {
    try {
      setLoading(true);
      const response = await api.get(`/business-managers/${record.id}`);
      setEditingBm(record);
      form.setFieldsValue({
        name: response.data.name,
        bm_id: response.data.bm_id,
        app_id: response.data.app_id || '',
        app_secret: response.data.app_secret || '',
        access_token: response.data.access_token || '',
        status: response.data.status,
      });
      setModalVisible(true);
    } catch (error) {
      message.error('获取 BM 详情失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'BM ID',
      dataIndex: 'bm_id',
      key: 'bm_id',
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '正常' : '停用'}
        </Tag>
      ),
    },
    {
      title: '开发者账号',
      dataIndex: 'developer_account_count',
      key: 'developer_account_count',
      render: (count, record) => (
        <Button 
          type="link" 
          onClick={() => handleViewDevAccounts(record)}
          style={{ padding: 0 }}
        >
          <Tag color={count > 0 ? 'blue' : 'default'} style={{ cursor: 'pointer' }}>
            {count || 0} 个
          </Tag>
        </Button>
      ),
    },
    {
      title: '用户',
      dataIndex: 'user_count',
      key: 'user_count',
      render: (count) => (
        <Tag color={count > 0 ? 'purple' : 'default'}>
          {count || 0} 个
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleViewAssets(record)}
          >
            资产 ({record.pixel_count || 0}/{record.page_count || 0}/{record.account_count || 0})
          </Button>
          <Button
            icon={<SyncOutlined />}
            onClick={() => handleSync(record.id)}
          >
            同步资产
          </Button>
          <Button
            icon={<UserOutlined />}
            loading={syncUsersLoading === record.id}
            onClick={() => handleSyncUsers(record.id)}
          >
            同步用户
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除吗？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button icon={<DeleteOutlined />} danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="BM 资产管理"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingBm(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            添加 BM
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={bms}
          rowKey="id"
          loading={loading}
        />
      </Card>

      {/* 创建/编辑模态框 */}
      <Modal
        title={editingBm ? '编辑 BM' : '添加 BM'}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingBm(null);
        }}
        width={600}
      >
        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="BM 名称"
            rules={[{ required: true, message: '请输入 BM 名称' }]}
          >
            <Input placeholder="输入 BM 名称" />
          </Form.Item>
          <Form.Item
            name="bm_id"
            label="BM ID"
            rules={[{ required: true, message: '请输入 BM ID' }]}
          >
            <Input placeholder="例如：1234567890" />
          </Form.Item>
          <Form.Item
            name="app_id"
            label="App ID"
          >
            <Input placeholder="Facebook App ID" />
          </Form.Item>
          <Form.Item
            name="app_secret"
            label="App Secret"
          >
            <Input.Password placeholder="Facebook App Secret" />
          </Form.Item>
          <Form.Item
            name="access_token"
            label="Access Token"
          >
            <Input.TextArea placeholder="Facebook Access Token" rows={3} />
          </Form.Item>
          <Form.Item
            name="status"
            label="状态"
            initialValue="active"
          >
            <Select>
              <Option value="active">正常</Option>
              <Option value="paused">暂停</Option>
              <Option value="disabled">停用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 资产详情模态框 */}
      <Modal
        title={`${selectedBm?.name} - 资产详情`}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        width={800}
        footer={null}
      >
        <Tabs defaultActiveKey="developer_accounts">
          <TabPane tab={`开发者账号 (${assets.developer_accounts?.length || 0})`} key="developer_accounts">
            <Table
              dataSource={assets.developer_accounts || []}
              rowKey="id"
              columns={[
                { title: 'ID', dataIndex: 'id' },
                { title: '名称', dataIndex: 'name' },
                { title: 'App ID', dataIndex: 'app_id' },
                { 
                  title: 'API 配额', 
                  key: 'quota',
                  render: (_, record) => (
                    <span>{record.daily_api_calls} / {record.daily_api_limit}</span>
                  )
                },
                { 
                  title: '状态', 
                  dataIndex: 'status',
                  render: (status) => (
                    <Tag color={status === 'active' ? 'green' : 'red'}>
                      {status === 'active' ? '正常' : '停用'}
                    </Tag>
                  )
                },
              ]}
              pagination={false}
            />
          </TabPane>
          <TabPane tab={`用户 (${assets.users?.length || 0})`} key="users">
            <Table
              dataSource={assets.users || []}
              rowKey="id"
              columns={[
                { title: 'Facebook 用户 ID', dataIndex: 'facebook_user_id' },
                { title: '名称', dataIndex: 'name' },
                { title: '邮箱', dataIndex: 'email', render: (v) => v || '-' },
                { title: '角色', dataIndex: 'role', render: (v) => v || '-' },
                {
                  title: '用户类型',
                  dataIndex: 'is_system_user',
                  render: (isSystem) => (
                    <Tag color={isSystem ? 'orange' : 'blue'}>
                      {isSystem ? '系统用户' : '普通用户'}
                    </Tag>
                  ),
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  render: (s) => (
                    <Tag color={s === 'active' ? 'green' : 'red'}>{s}</Tag>
                  ),
                },
              ]}
              pagination={false}
            />
          </TabPane>
          <TabPane tab="像素" key="pixels">
            <Table
              dataSource={assets.pixels}
              rowKey="id"
              columns={[
                { title: '像素 ID', dataIndex: 'pixel_id' },
                { title: '名称', dataIndex: 'name' },
                { title: '状态', dataIndex: 'status', render: (s) => <Tag color="green">{s}</Tag> },
              ]}
              pagination={false}
            />
          </TabPane>
          <TabPane tab="主页" key="pages">
            <Table
              dataSource={assets.pages}
              rowKey="id"
              columns={[
                { title: '主页 ID', dataIndex: 'page_id' },
                { title: '名称', dataIndex: 'name' },
                { title: '状态', dataIndex: 'status', render: (s) => <Tag color="green">{s}</Tag> },
              ]}
              pagination={false}
            />
          </TabPane>
          <TabPane tab="广告账户" key="accounts">
            <Table
              dataSource={assets.ad_accounts}
              rowKey="id"
              columns={[
                { title: '账户 ID', dataIndex: 'account_id' },
                { title: '名称', dataIndex: 'name' },
                { 
                  title: 'BM 信息', 
                  key: 'bm_info',
                  render: (_, record) => (
                    <div style={{ lineHeight: '1.5' }}>
                      <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
                        {selectedBm?.bm_id || '-'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {selectedBm?.name || '-'}
                      </div>
                    </div>
                  )
                },
              ]}
              pagination={false}
            />
          </TabPane>
        </Tabs>
      </Modal>

      {/* 开发者账号列表弹窗 */}
      <Modal
        title={`${selectedBm?.name} - 关联的开发者账号`}
        open={devAccountModalVisible}
        onCancel={() => setDevAccountModalVisible(false)}
        width={700}
        footer={null}
      >
        <Table
          dataSource={devAccountList}
          rowKey="id"
          loading={devAccountLoading}
          columns={[
            { title: 'ID', dataIndex: 'id', width: 60 },
            { title: '名称', dataIndex: 'name' },
            { title: 'App ID', dataIndex: 'app_id' },
            { 
              title: 'BM数量', 
              dataIndex: 'bm_count',
              render: (count) => (
                <Tag color="blue">{count || 0} 个BM</Tag>
              )
            },
            { 
              title: 'API 配额', 
              key: 'quota',
              render: (_, record) => (
                <span>{record.daily_api_calls || 0} / {record.daily_api_limit || 1000}</span>
              )
            },
            { 
              title: '状态', 
              dataIndex: 'status',
              width: 80,
              render: (status) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                  {status === 'active' ? '正常' : '停用'}
                </Tag>
              )
            },
          ]}
          pagination={false}
        />
      </Modal>
    </div>
  );
};

export default BusinessManagers;
